import { computed, ref, watch, type ComputedRef } from 'vue';

const NO_KEYS: readonly string[] = Object.freeze([]);

/** Key of an open row's detail in the virtual list's extra-row measurements. */
export function rowDetailKey(rowId: string): string {
  return `detail:${rowId}`;
}

/**
 * Open/closed state of the row details (`NxTable` `expandableRows`), keyed by
 * row id so it survives sorting, filtering and refetches. A row that leaves
 * `data` closes; turning the feature off closes everything.
 */
export function useRowExpansion<T>(params: {
  /** Rows in display order. */
  data: ComputedRef<T[]>;
  rowIdKey: (row: T) => string;
  enabled: ComputedRef<boolean>;
  /** Whether `row` can open at all. */
  isExpandable: (row: T) => boolean;
  onChange: (expandedIds: Set<string>) => void;
}) {
  const expandedIds = ref<Set<string>>(new Set());

  function commit(next: Set<string>): void {
    expandedIds.value = next;
    params.onChange(new Set(next));
  }

  function isExpanded(rowId: string): boolean {
    return expandedIds.value.has(rowId);
  }

  function setRowExpanded(rowId: string, expanded: boolean): void {
    if (expanded === isExpanded(rowId)) return;
    if (expanded) {
      if (!params.enabled.value) return;
      const row = params.data.value.find((r) => r && params.rowIdKey(r) === rowId);
      if (!row || !params.isExpandable(row)) return;
    }
    const next = new Set(expandedIds.value);
    if (expanded) next.add(rowId);
    else next.delete(rowId);
    commit(next);
  }

  function toggleRowExpanded(rowId: string): void {
    setRowExpanded(rowId, !isExpanded(rowId));
  }

  function collapseAllRows(): void {
    if (expandedIds.value.size > 0) commit(new Set());
  }

  // Rows that left the data close. Only a real change assigns a new set, so a
  // refetch of the same rows keeps the identity (and emits nothing).
  watch(
    () => params.data.value,
    (rows) => {
      if (expandedIds.value.size === 0) return;
      const live = new Set<string>();
      for (const row of rows) if (row) live.add(params.rowIdKey(row));
      const kept = [...expandedIds.value].filter((id) => live.has(id));
      if (kept.length !== expandedIds.value.size) commit(new Set(kept));
    },
    { flush: 'post' }
  );

  watch(params.enabled, (on) => {
    if (!on) collapseAllRows();
  });

  const hasExpanded = computed(() => expandedIds.value.size > 0);

  /** Display indices of the open rows, ascending. */
  const expandedIndices = computed(() => {
    const out: number[] = [];
    if (expandedIds.value.size === 0) return out;
    params.data.value.forEach((row, i) => {
      if (row && expandedIds.value.has(params.rowIdKey(row))) out.push(i);
    });
    return out;
  });

  /** Open rows that are in `data` — each is one more grid row (`aria-rowcount`). */
  const expandedCount = computed(() => expandedIndices.value.length);

  /** Open rows above display index `index` — each adds one grid row (`aria-rowindex`). */
  function expandedBefore(index: number): number {
    const list = expandedIndices.value;
    let lo = 0;
    let hi = list.length;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      if (list[mid] < index) lo = mid + 1;
      else hi = mid;
    }
    return lo;
  }

  /** The detail row below the row `rowId`, for the virtual list's slot heights. */
  function trailingKeysOf(rowId: string): readonly string[] {
    return expandedIds.value.has(rowId) ? [rowDetailKey(rowId)] : NO_KEYS;
  }

  return {
    expandedIds,
    hasExpanded,
    isExpanded,
    setRowExpanded,
    toggleRowExpanded,
    collapseAllRows,
    expandedCount,
    expandedBefore,
    trailingKeysOf,
  };
}
